import React from "react";
import { Box, Typography, Grid, Button, Stack } from "@mui/material";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { fadeUp } from "../../utils/animations";

const highlights = [
  { label: "Talent database", meta: "12,000+ profiles" },
  { label: "Recruitment requests", meta: "Live pipeline tracking" },
  { label: "Candidate profiles", meta: "Verified & screened" },
  { label: "Hiring dashboard", meta: "Real-time visibility" },
];

const stats = [
  { value: "98%", label: "Placement readiness" },
  { value: "4.6x", label: "Faster shortlisting" },
  { value: "30+", label: "Enterprise partners" },
];

export default function TalentHero() {
  return (
    <Box component="section" sx={{ bgcolor: "#0A0F1E", pt: 18, pb: 10, color: "white", position: "relative", overflow: "hidden" }}>
      {/* Background layers */}
      <Box className="tech-grid" sx={{ position: "absolute", inset: 0, opacity: 0.15, pointerEvents: "none" }} />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: "-18%",
          left: "8%",
          width: 520,
          height: 520,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37,99,235,0.32) 0%, transparent 70%)",
          filter: "blur(10px)",
          pointerEvents: "none",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          bottom: "-25%",
          right: "-5%",
          width: 480,
          height: 480,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(34,197,94,0.16) 0%, transparent 70%)",
          filter: "blur(10px)",
          pointerEvents: "none",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(180deg, transparent 0%, rgba(10,15,30,0.6) 85%, #0A0F1E 100%)",
          pointerEvents: "none",
        }}
      />

      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, position: "relative" }}>
        <Grid container spacing={6} alignItems="flex-start">
          <Grid item xs={12} lg={6}>
            <motion.div {...fadeUp()}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "9px",
                    bgcolor: "rgba(37,99,235,0.16)",
                    border: "1px solid rgba(37,99,235,0.35)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Sparkles size={15} color="#60A5FA" strokeWidth={2.2} />
                </Box>
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 700, letterSpacing: "0.18em", color: "rgba(255,255,255,0.6)", fontSize: 12 }}
                >
                  Talent division
                </Typography>
              </Stack>
            </motion.div>

            <motion.div {...fadeUp(0.08)}>
              <Typography
                variant="h2"
                fontWeight={700}
                sx={{
                  fontSize: { xs: "2.1rem", md: "3.1rem", lg: "3.6rem" },
                  lineHeight: 1.12,
                  letterSpacing: "-0.02em",
                  mb: 2.5,
                }}
              >
                Hire certified, prepared, and{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(90deg, #60A5FA, #93C5FD)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  role-ready
                </Box>{" "}
                technology talent.
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.14)}>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.62)", lineHeight: 1.8, fontSize: 16.5, maxWidth: 540, mb: 4 }}>
                Recruitment support, talent acquisition, workforce development, candidate screening, hiring programs, talent database, and employer dashboards — in one connected system.
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.2)}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: { xs: 5, lg: 6 } }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowRight size={18} />}
                  sx={{
                    bgcolor: "#2563EB",
                    color: "white",
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: 15,
                    px: 3.25,
                    py: 1.4,
                    borderRadius: "11px",
                    boxShadow: "0 16px 32px -14px rgba(37,99,235,0.55)",
                    "&:hover": { bgcolor: "#1D4ED8" },
                  }}
                >
                  Request talent
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    color: "white",
                    borderColor: "rgba(255,255,255,0.22)",
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: 15,
                    px: 3.25,
                    py: 1.4,
                    borderRadius: "11px",
                    "&:hover": { borderColor: "rgba(255,255,255,0.4)", bgcolor: "rgba(255,255,255,0.04)" },
                  }}
                >
                  View dashboard
                </Button>
              </Stack>
            </motion.div>

            <motion.div {...fadeUp(0.26)}>
              <Stack direction="row" spacing={{ xs: 3, sm: 5 }} flexWrap="wrap">
                {stats.map((s, idx) => (
                  <Box key={s.label} sx={{ pr: idx < stats.length - 1 ? { xs: 3, sm: 5 } : 0, borderRight: idx < stats.length - 1 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
                    <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, letterSpacing: "-0.02em", color: "white", lineHeight: 1 }}>
                      {s.value}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.5)", mt: 0.6 }}>{s.label}</Typography>
                  </Box>
                ))}
              </Stack>
            </motion.div>
          </Grid>

          <Grid item xs={12} lg={6}>
            <motion.div {...fadeUp(0.16)} style={{ width: "100%" }}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: "22px",
                  p: { xs: 2.5, md: 3 },
                  bgcolor: "rgba(255,255,255,0.05)",
                  backdropFilter: "blur(16px)",
                  boxShadow: "0 30px 60px -30px rgba(0,0,0,0.6)",
                  overflow: "hidden",
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    top: -40,
                    right: -40,
                    width: 160,
                    height: 160,
                    borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(37,99,235,0.25) 0%, transparent 70%)",
                  }}
                />

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, px: 0.5 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "rgba(255,255,255,0.5)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                    Platform modules
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#22C55E", boxShadow: "0 0 0 4px rgba(34,197,94,0.18)" }} />
                    <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.5)", fontWeight: 600 }}>Live</Typography>
                  </Box>
                </Stack>

                <Box sx={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  {highlights.map((item, i) => (
                    <Box
                      key={item.label}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        bgcolor: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.06)",
                        borderRadius: "14px",
                        p: 2.25,
                        mb: i === highlights.length - 1 ? 0 : 1.4,
                        transition: "background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease",
                        "&:hover": {
                          bgcolor: "rgba(255,255,255,0.09)",
                          borderColor: "rgba(255,255,255,0.14)",
                          transform: "translateX(2px)",
                        },
                      }}
                    >
                      <Box>
                        <Typography variant="body1" fontWeight={700} color="white" sx={{ fontSize: 14.5, mb: 0.3 }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>{item.meta}</Typography>
                      </Box>
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          bgcolor: "rgba(34,197,94,0.14)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <CheckCircle2 size={17} color="#22C55E" strokeWidth={2.3} />
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    mt: 2.25,
                    pt: 2.25,
                    borderTop: "1px solid rgba(255,255,255,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)", mb: 0.4 }}>
                      All modules syncing in real time
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "white" }}>
                      4 of 4 systems connected
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.6 }}>
                    {highlights.map((item) => (
                      <Box
                        key={item.label}
                        sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#22C55E", boxShadow: "0 0 0 3px rgba(34,197,94,0.16)" }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}